import type { AxiosHeaders } from 'axios';
import { api } from './index';
import { type PaymentMovement } from './payment-movements';

const BASE_PATH = '/api/internal/payments';

// Slim shapes returned by the payments index endpoint. The index serializes only
// the fields consumed by the payments screen and the xlsx export, not the full
// Caregiver/Patient payloads (see Api::Internal::PaymentCaregiverSerializer and
// Api::Internal::PaymentSummaryPatientSerializer).
export interface PaymentCaregiver {
  id: number;
  firstName: string;
  lastName: string;
  fullName: string;
  dni: string;
  email: string;
  phoneNumber: string;
}

export interface PaymentSummaryPatient {
  id: number;
  fullName: string;
}

export interface ShiftSummary {
  shiftsCount: number;
  shiftsCountWithTaxes: number;
  cost: number;
  taxes: number;
  totalNetCost: number;
  totalNetTaxes: number;
  totalGrossAmount: number;
}

export interface PaymentSummary {
  patient: PaymentSummaryPatient;
  shiftType: string;
  shiftsSummary: Array<ShiftSummary>;
}

export interface SalaryBreakdown {
  salaryId: number;
  effectiveStart: string;
  effectiveEnd: string;
  effectiveDays: number;
  baseSalary: number;
  totalMonthDays: number;
  proratedBaseSalary: number;
  shiftsInPeriod: number;
  shiftsWithVariable: number;
  minimumShiftsThreshold: number;
  amountPerShift: number;
  variableSalary: number;
  total: number;
}

export interface SalaryPayment {
  shiftsCount: number;
  baseSalary: number;
  variableSalary: number;
  totalEffectiveDays: number;
  totalShiftsWithVariable: number;
  salaryBreakdown: Array<SalaryBreakdown>;
  cost: number;
}

export interface PerShiftPayment {
  shiftsCount: number;
  shiftsCountWithTaxes: number;
  paymentsSummary: Array<PaymentSummary>;
  cost: number;
  taxes: number;
  totalCost: number;
  taxedCost: number;
  grossCost: number;
}

export type Payment = {
  caregiver: PaymentCaregiver;
  shiftsCount: number;
  checkedInShiftsCount: number;
  cost: number;
  taxes: number;
  totalCost: number;
  // The movements in the tax bill: their net amount and the withholding they add.
  taxedMovements: number;
  movementsTaxes: number;
  paymentsSummary: Array<PaymentSummary>;
  lastEmailSentAt: string;
  paymentMovements: Array<PaymentMovement>;
  totalPaymentMovements: number;
  amountPendingToPay: number;
  salaryPayment?: SalaryPayment;
  perShiftPayment?: PerShiftPayment;
}

export interface PDFPayment {
  headers: AxiosHeaders;
  data: Blob;
}

export interface XlsxPayments {
  headers: AxiosHeaders;
  data: Blob;
}

// Pagos' bulk send: the payments email of the period to each caregiver, queued on
// the server. Caregivers without an email are left out and counted.
export interface PaymentEmailsResult {
  queued: number;
  skippedWithoutEmail: number;
}

export const paymentsApi = {
  index(queryParams: string): Promise<Array<Payment>> {
    return api({
      method: 'get',
      url: `${BASE_PATH}?${queryParams}`,
    }).then(response => response.data.payments);
  },
  indexAsXlsx(queryParams: string): Promise<XlsxPayments> {
    return api({
      method: 'get',
      url: `${BASE_PATH}.xlsx?${queryParams}`,
      responseType: 'blob',
    });
  },
  show(id: number, queryParams: string, format: string): Promise<PDFPayment> {
    return api({
      method: 'get',
      url: `${BASE_PATH}/${id}.${format}?${queryParams}`,
      responseType: format === 'pdf' ? 'blob' : 'json',
    });
  },
  sendEmails(data: { caregiverIds: number[]; fromDate: string; toDate: string }): Promise<PaymentEmailsResult> {
    return api({
      method: 'post',
      url: '/api/internal/payment_emails',
      data,
    }).then(response => response.data.paymentEmail);
  },
};
